import { CartLinesUpdateEvent } from '@shopify/events';

const DEFAULT_TITLE = 'Default Title';
const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

class ForgeYourSet extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    const designs = this.#buildDesigns();
    if (designs.length === 0) {
      this.hidden = true;
      return;
    }

    this.designs = designs;
    this.formatter = new Intl.NumberFormat(this.dataset.locale || undefined, {
      style: 'currency',
      currency: this.dataset.currency || 'USD',
    });

    this.picker = this.querySelector('[data-forge-picker]');
    this.plus = this.querySelector('[data-forge-plus]');
    this.totalEl = this.querySelector('[data-forge-total]');
    this.addButton = this.querySelector('[data-forge-add]');
    this.errorEl = this.querySelector('[data-forge-error]');
    this.liveEl = this.querySelector('[data-forge-live]');
    this.cards = {
      cloak: this.#cardRefs('cloak'),
      trousers: this.#cardRefs('trousers'),
    };

    this.state = { design: -1, line: 0, cloakSize: null, trousersSize: null };
    this.displayedTotal = 0;

    this.#renderPicker();
    this.addButton.addEventListener('click', () => this.#addSetToCart());
    this.selectDesign(0, false);
  }

  #buildDesigns() {
    const read = (selector) => {
      try {
        return JSON.parse(this.querySelector(selector)?.textContent || '[]');
      } catch {
        return [];
      }
    };
    const key = (name) => (name || '').trim().toLowerCase();

    const trousers = read('[data-forge-trousers]');
    const cloaks = [
      ...read('[data-forge-imperial]').map((p) => ({ ...p, line: this.dataset.imperialLabel })),
      ...read('[data-forge-mythic]').map((p) => ({ ...p, line: this.dataset.mythicLabel })),
      ...read('[data-forge-eternal]').map((p) => ({ ...p, line: this.dataset.eternalLabel })),
    ];

    const seen = new Set();
    return trousers
      .filter((t) => t.available && !seen.has(key(t.name)) && seen.add(key(t.name)))
      .map((t) => ({
        name: t.name,
        trousers: t,
        cloaks: cloaks.filter((c) => c.available && key(c.name) === key(t.name)),
      }))
      .filter((d) => d.cloaks.length > 0);
  }

  #cardRefs(type) {
    const card = this.querySelector(`[data-forge-card="${type}"]`);
    return {
      img: card.querySelector('[data-forge-img]'),
      title: card.querySelector('[data-forge-title]'),
      lines: card.querySelector('[data-forge-lines]'),
      sizes: card.querySelector('[data-forge-sizes]'),
      price: card.querySelector('[data-forge-price]'),
    };
  }

  #renderPicker() {
    this.swatches = this.designs.map((design, i) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'forge__swatch';
      button.setAttribute('aria-pressed', 'false');

      const imgWrap = document.createElement('span');
      imgWrap.className = 'forge__swatch-img';
      const img = document.createElement('img');
      img.src = design.cloaks[0].image;
      img.alt = '';
      img.loading = 'lazy';
      img.width = 64;
      img.height = 64;
      imgWrap.append(img);

      const name = document.createElement('span');
      name.className = 'forge__swatch-name';
      name.textContent = design.name;

      button.append(imgWrap, name);
      button.addEventListener('click', () => this.selectDesign(i, true));
      this.picker.append(button);
      return button;
    });
  }

  selectDesign(index, animate) {
    if (index === this.state.design) return;
    const design = this.designs[index];
    this.state.design = index;

    // Default to the first line (Imperial when the design has one); keep a line only if the customer chose it.
    const preferred = design.cloaks.findIndex((c) => c.line === this.preferredLine);
    this.state.line = preferred >= 0 ? preferred : 0;

    this.swatches.forEach((swatch, i) => swatch.setAttribute('aria-pressed', i === index ? 'true' : 'false'));
    const active = this.swatches[index];
    if (this.picker.scrollWidth > this.picker.clientWidth) {
      this.picker.scrollTo({ left: active.offsetLeft - this.picker.offsetLeft - 16, behavior: 'smooth' });
    }

    this.#renderCloak(animate);
    this.#renderCard('trousers', design.trousers, animate);
    if (animate) this.#forge();
    this.#updateSummary();
  }

  get currentDesign() {
    return this.designs[this.state.design];
  }

  get currentCloak() {
    const design = this.currentDesign;
    return design ? design.cloaks[this.state.line] : null;
  }

  #renderCloak(animate) {
    const design = this.currentDesign;
    const { lines } = this.cards.cloak;
    lines.replaceChildren();

    if (design.cloaks.length > 1) {
      design.cloaks.forEach((cloak, i) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'forge__line';
        button.textContent = cloak.line;
        button.setAttribute('aria-pressed', i === this.state.line ? 'true' : 'false');
        button.addEventListener('click', () => {
          if (i === this.state.line) return;
          this.state.line = i;
          this.preferredLine = cloak.line;
          this.#renderCloak(true);
          this.#updateSummary();
        });
        lines.append(button);
      });
    }

    this.#renderCard('cloak', this.currentCloak, animate);
    this.#syncSizeGuide();
  }

  #syncSizeGuide() {
    const guides = Array.from(this.querySelectorAll('[data-forge-card="cloak"] [data-forge-guide]'));
    if (guides.length === 0) return;
    const line = this.currentCloak.line;
    const match = guides.find((g) => g.dataset.forgeGuide === line) || guides[0];
    guides.forEach((g) => (g.hidden = g !== match));
  }

  #renderCard(type, product, animate) {
    const refs = this.cards[type];
    const sizeKey = type === 'cloak' ? 'cloakSize' : 'trousersSize';

    this.#swapImage(refs.img, product.image, product.title, animate);
    refs.title.textContent = product.title;
    refs.title.href = product.url;

    const single = product.variants.length === 1 && product.variants[0].title === DEFAULT_TITLE;
    const remembered = this.state[sizeKey];
    const keep = product.variants.find((v) => v.title === remembered && v.available);
    this.state[sizeKey] = single ? product.variants[0].title : keep ? keep.title : null;

    refs.sizes.replaceChildren();
    if (!single) {
      product.variants.forEach((variant) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'forge__size';
        button.textContent = variant.title;
        button.disabled = !variant.available;
        button.setAttribute('aria-pressed', variant.title === this.state[sizeKey] ? 'true' : 'false');
        button.setAttribute('aria-label', `${product.title}, ${variant.title}`);
        button.addEventListener('click', () => {
          this.state[sizeKey] = variant.title;
          refs.sizes
            .querySelectorAll('.forge__size')
            .forEach((b) => b.setAttribute('aria-pressed', b === button ? 'true' : 'false'));
          this.#updatePrice(type);
          this.#updateSummary();
        });
        refs.sizes.append(button);
      });
    }

    this.#updatePrice(type);
  }

  #swapImage(img, src, alt, animate) {
    if (!src) return;
    if (!animate || reducedMotion() || !img.getAttribute('src')) {
      img.src = src;
      img.alt = alt;
      return;
    }
    img.classList.add('is-swapping');
    window.setTimeout(() => {
      const done = () => img.classList.remove('is-swapping');
      img.addEventListener('load', done, { once: true });
      img.addEventListener('error', done, { once: true });
      img.src = src;
      img.alt = alt;
      if (img.complete) done();
    }, 220);
  }

  #selectedVariant(type) {
    const product = type === 'cloak' ? this.currentCloak : this.currentDesign.trousers;
    const size = this.state[type === 'cloak' ? 'cloakSize' : 'trousersSize'];
    return product.variants.find((v) => v.title === size && v.available) || null;
  }

  #displayVariant(type) {
    const selected = this.#selectedVariant(type);
    if (selected) return selected;
    const product = type === 'cloak' ? this.currentCloak : this.currentDesign.trousers;
    const available = product.variants.filter((v) => v.available);
    const pool = available.length ? available : product.variants;
    return pool.reduce((min, v) => (v.price < min.price ? v : min), pool[0]);
  }

  #regularPrice(variant) {
    return variant.compare > variant.price ? variant.compare : variant.price;
  }

  #updatePrice(type) {
    const variant = this.#displayVariant(type);
    const el = this.cards[type].price;
    const price = document.createElement('span');
    price.textContent = this.#money(variant.price);
    el.replaceChildren(price);
    if (variant.compare > variant.price) {
      const compare = document.createElement('s');
      compare.textContent = this.#money(variant.compare);
      el.append(compare);
    }
  }

  #updateSummary() {
    const cloak = this.#selectedVariant('cloak');
    const trousers = this.#selectedVariant('trousers');
    const shownCloak = this.#displayVariant('cloak');
    const shownTrousers = this.#displayVariant('trousers');
    const total = shownCloak.price + shownTrousers.price;
    const regular = this.#regularPrice(shownCloak) + this.#regularPrice(shownTrousers);

    this.#animateTotal(total);
    const compareEl = this.querySelector('[data-forge-total-compare]');
    const saveEl = this.querySelector('[data-forge-save]');
    if (regular > total) {
      compareEl.textContent = this.#money(regular);
      saveEl.textContent = `${this.dataset.labelSave || 'You save'} ${this.#money(regular - total)}`;
      saveEl.hidden = false;
    } else {
      compareEl.textContent = '';
      saveEl.hidden = true;
    }
    this.#hideError();

    if (this.addButton.classList.contains('is-loading')) return;
    this.addButton.classList.remove('is-added');
    const ready = Boolean(cloak && trousers);
    this.addButton.disabled = !ready;
    this.addButton.textContent = ready ? this.dataset.labelAdd : this.dataset.labelSelect;
  }

  #animateTotal(target) {
    const from = this.displayedTotal;
    this.displayedTotal = target;
    if (from === 0 || from === target || reducedMotion()) {
      this.totalEl.textContent = this.#money(target);
      return;
    }
    const start = performance.now();
    const duration = 550;
    cancelAnimationFrame(this.totalFrame);
    const step = (now) => {
      const t = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - t, 3);
      this.totalEl.textContent = this.#money(Math.round(from + (target - from) * eased));
      if (t < 1) this.totalFrame = requestAnimationFrame(step);
    };
    this.totalFrame = requestAnimationFrame(step);
  }

  #forge() {
    if (!this.plus || reducedMotion()) return;
    this.plus.classList.remove('is-forging');
    void this.plus.offsetWidth;
    this.plus.classList.add('is-forging');

    const count = 12;
    for (let i = 0; i < count; i++) {
      const spark = document.createElement('span');
      spark.className = 'forge__spark';
      const angle = (Math.PI * 2 * i) / count + Math.random() * 0.4;
      const distance = 34 + Math.random() * 30;
      spark.style.setProperty('--dx', `${Math.cos(angle) * distance}px`);
      spark.style.setProperty('--dy', `${Math.sin(angle) * distance}px`);
      spark.addEventListener('animationend', () => spark.remove(), { once: true });
      this.plus.append(spark);
    }
  }

  #money(cents) {
    return this.formatter.format(cents / 100);
  }

  #showError(message) {
    this.errorEl.textContent = message;
    this.errorEl.hidden = false;
  }

  #hideError() {
    this.errorEl.hidden = true;
    this.errorEl.textContent = '';
  }

  async #addSetToCart() {
    const cloak = this.#selectedVariant('cloak');
    const trousers = this.#selectedVariant('trousers');
    if (!cloak || !trousers || this.addButton.classList.contains('is-loading')) return;

    this.#hideError();
    this.addButton.classList.add('is-loading');
    this.addButton.textContent = this.dataset.labelAdding;

    const root = window.Shopify?.routes?.root || '/';
    const ids = [cloak.id, trousers.id];
    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((el) => el.dataset.sectionId)
      .filter(Boolean);

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: ids.map((id) => ({ merchandiseId: String(id), quantity: 1 })),
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: ids.map((id) => ({ id, quantity: 1 })),
          sections: sectionIds.join(','),
        }),
      });
      const data = await response.json();
      const ajaxCart = await fetch(`${root}cart.js`).then((r) => r.json());
      const failed = !response.ok || Boolean(data.status);

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
        detail: {
          items: ajaxCart.items,
          source: 'forge-your-set',
          sourceId: this.id || 'forge-your-set',
          itemCount: ids.length,
          sections: failed ? undefined : data.sections,
          didError: failed,
        },
      });

      this.addButton.classList.remove('is-loading');
      if (failed) {
        this.#updateSummary();
        this.#showError(data.description || data.message || 'Something went wrong. Please try again.');
        return;
      }

      this.addButton.classList.add('is-added');
      this.addButton.textContent = this.dataset.labelAdded;
      this.liveEl.textContent = this.dataset.labelAdded;
      window.setTimeout(() => {
        this.liveEl.textContent = '';
        if (this.addButton.classList.contains('is-added')) this.#updateSummary();
      }, 2600);
    } catch (error) {
      deferred.reject(error);
      this.addButton.classList.remove('is-loading');
      this.#updateSummary();
      this.#showError('Something went wrong. Please try again.');
    }
  }
}

if (!customElements.get('forge-your-set')) {
  customElements.define('forge-your-set', ForgeYourSet);
}
